import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './navbar';

const COMPANIES = [
  { id: 'ACME001', name: 'Acme Corporation' },
  { id: 'ACES001', name: 'Acespire Solutions' },
  { id: 'DEMO001', name: 'Demo Company' },
  { id: 'XYZ001', name: 'XYZ Supplier' },
  { id: 'NAV001', name: 'Navneet' },
];

const MODEL_FIELDS = [
  ['modelName', 'Model Name', 'String'],
  ['modelNumber', 'Model Number', 'String'],
  ['chemistry', 'Chemistry', 'String'],
  ['capacity', 'Capacity', 'String'],
  ['nominalVoltage', 'Nominal Voltage', 'String'],
];

const BATCH_FIELDS = [
  ['batchNumber', 'Batch Number', 'String'],
  ['productionDate', 'Production Date', 'Date'],
  ['facility', 'Facility', 'String'],
  ['carbonFootprint', 'Carbon Footprint', 'Number'],
  ['recycledContent', 'Recycled Content', 'Number'],
];

const PRODUCT_FIELDS = [
  ['productIdentifier', 'Product Identifier', 'String'],
  ['passportIdentifier', 'Battery Passport Identifier', 'String'],
  ['serialNumber', 'Serial Number', 'String'],
  ['batteryStatus', 'Battery Status', 'String'],
  ['dataCarrierUri', 'Data Carrier URI', 'String'],
];

const INITIAL_ADDITIONAL_FIELDS = [
  {
    id: 1,
    name: 'Additional Field 1',
    value: 'Value 1',
    type: 'String',
    include: true,
    category: 'Private',
  },
  {
    id: 2,
    name: 'Additional Field 2',
    value: '25',
    type: 'Number',
    include: true,
    category: 'Private',
  },
  {
    id: 3,
    name: 'Additional Field 3',
    value: '2026-07-15',
    type: 'Date',
    include: false,
    category: 'Private',
  },
  {
    id: 4,
    name: 'Additional Field 4',
    value: 'Value 4',
    type: 'String',
    include: true,
    category: 'Private',
  },
  {
    id: 5,
    name: 'Additional Field 5',
    value: 'Value 5',
    type: 'String',
    include: false,
    category: 'Private',
  },
];

const STEPS = [
  {
    title: 'Select Company',
    description:
      'Choose the company for which you want to create the product DPP.',
    icon: '▦',
    help: 'The selected company determines the applicable templates, data requirements and access permissions.',
  },
  {
    title: 'Select Product Category',
    description: 'Choose the category that best describes your product.',
    icon: '▦',
    help: 'The selected category determines the applicable template, data requirements and regulatory rules.',
  },
  {
    title: 'Select Template / Version',
    description: 'Choose the appropriate template and version for your product.',
    icon: '▤',
    help: 'Templates ensure your data follows the selected structure and applicable standards.',
  },
  {
    title: 'Upload Master Data',
    description: 'Upload your master data file as per the selected template.',
    icon: '⇧',
    help: 'Accurate master data is the foundation for generating a valid and compliant DPP.',
  },
  {
    title: 'System Auto-Fills Model Data',
    description: 'Review and edit the auto-filled model details before proceeding.',
    icon: '⚙',
    help: 'Accurate model data supports product identification and validation.',
  },
  {
    title: 'System Auto-Fills Batch Data',
    description: 'Review and edit the auto-filled batch details before proceeding.',
    icon: '⚙',
    help: 'Accurate batch data ensures traceability and supports a valid DPP.',
  },
  {
    title: 'System Auto-fills Product Data',
    description: 'Review and edit the auto-filled product details before proceeding.',
    icon: '⬡',
    help: 'Accurate product data helps identify the product and connect its digital passport.',
  },
  {
    title: 'Additional Data fields',
    description:
      'Review the additional data fields detected from your master data. You can edit the values and choose where to include them in the DPP.',
    icon: '▤',
    help: 'Additional data helps enrich your DPP with relevant information and can be included in specific sections based on your needs.',
  },
];

// Validate dates without timezone-related errors.
function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function validateAdditionalField(field) {
  const value = String(field.value ?? '').trim();

  if (field.type === 'Number') {
    return value !== '' && Number.isFinite(Number(value));
  }

  if (field.type === 'Date') {
    return isValidDate(value);
  }

  return value !== '';
}

function UploadData2() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [company, setCompany] = useState('ACME001');
  const [category, setCategory] = useState('Battery');
  const [template, setTemplate] = useState('Battery DPP Template v1.2');
  const [version, setVersion] = useState('V1.2.0 (Latest)');
  const [file, setFile] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const [modelData, setModelData] = useState({
    modelName: 'Lithium Battery Pack',
    modelNumber: 'LBP-4820',
    chemistry: 'Lithium Iron Phosphate',
    capacity: '48 kWh / 20 Ah',
    nominalVoltage: '48 V',
  });

  const [batchData, setBatchData] = useState({
    batchNumber: 'BATCH-2026-0001',
    productionDate: '2026-07-15',
    facility: 'Berlin Manufacturing Plant',
    carbonFootprint: '12.5',
    recycledContent: '28',
    carbonUnit: 'kg CO₂e',
  });

  const [productData, setProductData] = useState({
    productIdentifier: 'SDI-PROD-2026-000184',
    passportIdentifier: 'urn:uuid:bp-2026-000184',
    serialNumber: 'SDI-SN-2026-1849921',
    batteryStatus: 'Original',
    dataCarrierUri:
      'https://app.samsung.com/battery/passport/SDI-BP-2026-000184',
  });

  const [additionalFields, setAdditionalFields] = useState(
    INITIAL_ADDITIONAL_FIELDS
  );

  const inputClass =
    'w-full min-w-0 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200';

  const isFilled = (value) => String(value ?? '').trim() !== '';

  const closePage = () => navigate('/dashboard');

  const updateModel = (key, value) => {
    setModelData((current) => ({ ...current, [key]: value }));
  };

  const updateBatch = (key, value) => {
    setBatchData((current) => ({ ...current, [key]: value }));
  };

  const updateProduct = (key, value) => {
    setProductData((current) => ({ ...current, [key]: value }));
  };

  const updateAdditionalField = (id, key, value) => {
    setAdditionalFields((current) =>
      current.map((field) =>
        field.id === id ? { ...field, [key]: value } : field
      )
    );
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    const extension = selectedFile.name.split('.').pop().toLowerCase();

    if (!['csv', 'xlsx', 'xls'].includes(extension)) {
      alert('Please upload a CSV, XLSX, or XLS file.');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      alert('File size must not exceed 10 MB.');
      return;
    }

    setFile(selectedFile);
  };

  const goNext = () => {
    if (step === 3 && !file) {
      alert('Please upload your master data file.');
      return;
    }

    setStep((current) => Math.min(7, current + 1));
  };

  const goPrevious = () => {
    setStep((current) => Math.max(0, current - 1));
  };

  const validateModel = () =>
    Object.values(modelData).every(isFilled);

  const validateBatch = () => {
    const carbon = Number(batchData.carbonFootprint);
    const recycled = Number(batchData.recycledContent);

    return (
      isFilled(batchData.batchNumber) &&
      isValidDate(batchData.productionDate) &&
      isFilled(batchData.facility) &&
      batchData.carbonFootprint !== '' &&
      Number.isFinite(carbon) &&
      carbon >= 0 &&
      batchData.recycledContent !== '' &&
      Number.isFinite(recycled) &&
      recycled >= 0 &&
      recycled <= 100
    );
  };

  const validateProduct = () =>
    Object.values(productData).every(isFilled);

  const handleReview = () => {
    if (step === 4) {
      if (!validateModel()) {
        alert('Please complete all mandatory model data fields.');
        return;
      }

      setStep(5);
      return;
    }

    if (step === 5) {
      if (!validateBatch()) {
        alert(
          'Please check the batch fields. Recycled Content must be between 0 and 100, and the date must be valid.'
        );
        return;
      }

      setStep(6);
      return;
    }

    if (step === 6) {
      if (!validateProduct()) {
        alert('Please complete all mandatory product data fields.');
        return;
      }

      setStep(7);
    }
  };

  const createDPP = () => {
    const includedFields = additionalFields.filter(
      (field) => field.include
    );

    const invalidFields = includedFields.filter(
      (field) => !validateAdditionalField(field)
    );

    if (invalidFields.length > 0) {
      alert(
        `Please correct these fields: ${invalidFields
          .map((field) => field.name)
          .join(', ')}`
      );
      return;
    }

    console.log('DPP review data:', {
      company,
      category,
      template,
      version,
      fileName: file?.name,
      modelData,
      batchData,
      productData,
      additionalFields: includedFields,
    });

    alert(
      'Review completed. Actual DPP creation and backend submission are not connected yet.'
    );
  };

  const renderTemplateCard = () => (
    <>
      <div className="mt-4 flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-0 text-xs">
          <p className="text-slate-500">Selected Template</p>
          <p className="mt-1 font-semibold text-slate-800">{template}</p>
        </div>

        <button
          type="button"
          onClick={() => setShowDetails((current) => !current)}
          className="shrink-0 text-[10px] font-semibold text-blue-600 sm:text-xs"
        >
          ◉ View Template Details
        </button>
      </div>

      {showDetails && (
        <div className="mt-2 rounded-md bg-blue-50 p-3 text-xs text-slate-600">
          <p>Template: {template}</p>
          <p>Version: {version}</p>
          <p>Category: {category}</p>
        </div>
      )}
    </>
  );

  const renderTable = (fields, data, update) => (
    <div className="mt-2 overflow-x-auto rounded-md border border-slate-200">
      <table className="w-full min-w-[570px] border-collapse text-left text-[10px]">
        <thead className="bg-slate-100 text-slate-700">
          <tr>
            <th className="p-2">Field Name</th>
            <th className="p-2">Value (Editable)</th>
            <th className="p-2">Data Type</th>
            <th className="p-2">Mandatory</th>
            <th className="p-2">Status</th>
          </tr>
        </thead>

        <tbody>
          {fields.map(([key, label, type]) => {
            const value = data[key] ?? '';
            let valid = isFilled(value);

            if (key === 'productionDate') {
              valid = isValidDate(value);
            }

            if (key === 'carbonFootprint') {
              valid =
                value !== '' &&
                Number.isFinite(Number(value)) &&
                Number(value) >= 0;
            }

            if (key === 'recycledContent') {
              valid =
                value !== '' &&
                Number.isFinite(Number(value)) &&
                Number(value) >= 0 &&
                Number(value) <= 100;
            }

            return (
              <tr key={key} className="border-t border-slate-200">
                <td className="whitespace-nowrap p-2 font-medium">
                  {label}
                </td>

                <td className="p-1.5">
                  {key === 'productionDate' ? (
                    <input
                      type="date"
                      value={value}
                      onChange={(e) => update(key, e.target.value)}
                      className={inputClass}
                    />
                  ) : key === 'batteryStatus' ? (
                    <select
                      value={value}
                      onChange={(e) => update(key, e.target.value)}
                      className={inputClass}
                    >
                      <option>Original</option>
                      <option>In Use</option>
                      <option>Second Life</option>
                      <option>Recycled</option>
                    </select>
                  ) : key === 'carbonFootprint' ||
                    key === 'recycledContent' ? (
                    <div className="flex min-w-0 items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        max={
                          key === 'recycledContent' ? '100' : undefined
                        }
                        step="any"
                        value={value}
                        onChange={(e) => update(key, e.target.value)}
                        className={inputClass}
                      />

                      {key === 'carbonFootprint' ? (
                        <select
                          aria-label="Carbon footprint unit"
                          value={data.carbonUnit}
                          onChange={(e) =>
                            update('carbonUnit', e.target.value)
                          }
                          className="max-w-24 rounded border border-slate-300 bg-white px-1 text-[10px]"
                        >
                          <option>kg CO₂e</option>
                          <option>t CO₂e</option>
                        </select>
                      ) : (
                        <span className="rounded border border-slate-300 px-2 py-1">
                          %
                        </span>
                      )}
                    </div>
                  ) : (
                    <input
                      value={value}
                      onChange={(e) => update(key, e.target.value)}
                      className={inputClass}
                    />
                  )}
                </td>

                <td className="whitespace-nowrap p-2">{type}</td>

                <td className="whitespace-nowrap p-2">
                  <span className="text-green-600">✓</span> Yes
                </td>

                <td className="whitespace-nowrap p-2">
                  <span
                    className={
                      valid
                        ? 'font-semibold text-green-600'
                        : 'font-semibold text-red-600'
                    }
                  >
                    {valid ? '● Valid' : '● Invalid'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderAutoFillHeader = (heading) => (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs font-semibold text-slate-700">{heading}</p>
      <span className="rounded-full bg-green-50 px-2 py-1 text-[10px] font-semibold text-green-700">
        ✓ Auto-filled from master data (simulated)
      </span>
    </div>
  );

  const renderNote = () => (
    <div className="mt-3 rounded-md bg-blue-50 p-3 text-[10px] leading-4 text-slate-600">
      <p className="font-semibold text-slate-800">ⓘ Note</p>
      <p className="mt-1">
        Please review the auto-filled values and make changes if needed.
        All mandatory fields must be completed before proceeding.
      </p>
    </div>
  );

  const renderAdditionalFields = () => (
    <>
      <h2 className="pr-6 text-lg font-bold text-slate-800">
        Additional Data fields
      </h2>

      <p className="mt-1 text-xs leading-5 text-slate-500">
        Review the additional data fields detected from your master data.
        You can edit the values and choose where to include them in the DPP.
      </p>

      {renderTemplateCard()}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-700">
          Additional Data Fields (Auto-Filled from Master Data)
        </p>

        <span className="rounded-full bg-green-50 px-2 py-1 text-[10px] font-semibold text-green-700">
          ✓ Auto-filled from master data (simulated)
        </span>
      </div>

      <div className="mt-2 overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full min-w-[530px] border-collapse text-left text-[10px]">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="p-2">Field Name</th>
              <th className="p-2">Value (Editable)</th>
              <th className="p-2">Data Type</th>
              <th className="p-2 text-center">Include</th>
              <th className="p-2">Data Category</th>
              <th className="p-2">Status</th>
            </tr>
          </thead>

          <tbody>
            {additionalFields.map((field) => {
              const valid = validateAdditionalField(field);

              return (
                <tr key={field.id} className="border-t border-slate-200">
                  <td className="whitespace-nowrap p-2 font-medium">
                    {field.name}
                  </td>

                  <td className="p-1.5">
                    {field.type === 'Date' ? (
                      <input
                        type="date"
                        value={isValidDate(field.value) ? field.value : ''}
                        onChange={(e) =>
                          updateAdditionalField(
                            field.id,
                            'value',
                            e.target.value
                          )
                        }
                        className={inputClass}
                      />
                    ) : (
                      <input
                        type={field.type === 'Number' ? 'number' : 'text'}
                        value={field.value}
                        onChange={(e) =>
                          updateAdditionalField(
                            field.id,
                            'value',
                            e.target.value
                          )
                        }
                        className={inputClass}
                      />
                    )}
                  </td>

                  <td className="whitespace-nowrap p-2">{field.type}</td>

                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={field.include}
                      onChange={(e) =>
                        updateAdditionalField(
                          field.id,
                          'include',
                          e.target.checked
                        )
                      }
                      aria-label={`Include ${field.name}`}
                      className="h-3.5 w-3.5 accent-blue-600"
                    />
                  </td>

                  <td className="p-1.5">
                    <select
                      value={field.category}
                      onChange={(e) =>
                        updateAdditionalField(
                          field.id,
                          'category',
                          e.target.value
                        )
                      }
                      aria-label={`Data category for ${field.name}`}
                      className={inputClass}
                    >
                      <option>Private</option>
                      <option>Public</option>
                      <option>Business</option>
                      <option>Technical</option>
                      <option>Environmental</option>
                    </select>
                  </td>

                  <td className="whitespace-nowrap p-2">
                    <span
                      className={
                        valid
                          ? 'font-semibold text-green-600'
                          : 'font-semibold text-red-600'
                      }
                    >
                      {valid ? '● Valid' : '● Invalid'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 rounded-md bg-blue-50 p-3 text-[10px] leading-4 text-slate-600">
        <p className="font-semibold text-slate-800">ⓘ Note</p>
        <p className="mt-1">
          You can include or exclude these fields from the DPP and assign
          them to the appropriate data category. Make sure all included
          fields are valid before proceeding.
        </p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <Navbar />

      <main className="px-5 py-4">
        <h1 className="text-xl font-bold text-slate-800">
          Product DPP Creation
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Follow the guided steps to create a Digital Product Passport
          (DPP) for your product.
        </p>
      </main>

      <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-3">
        <div className="relative grid h-[min(680px,90vh)] w-full max-w-5xl grid-cols-1 overflow-hidden rounded-xl bg-white shadow-2xl md:grid-cols-[175px_minmax(0,1fr)]">
          <button
            type="button"
            onClick={closePage}
            aria-label="Close"
            className="absolute right-3 top-3 z-20 text-xl text-slate-500 hover:text-slate-900"
          >
            ×
          </button>

          <aside className="min-h-0 overflow-y-auto border-b bg-slate-50 p-4 md:border-b-0 md:border-r">
            <div className="text-center text-xs font-semibold text-blue-600">
              Step {step + 1} of 8
            </div>

            <div className="mx-auto mt-6 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-2xl text-blue-600">
              {STEPS[step].icon}
            </div>

            <h2 className="mt-3 text-center text-sm font-bold text-slate-800">
              {STEPS[step].title}
            </h2>

            <p className="mt-2 text-center text-xs leading-5 text-slate-500">
              {STEPS[step].description}
            </p>

            <div className="mt-4 rounded-lg bg-blue-50 p-3 text-xs leading-5 text-slate-600">
              <p className="font-semibold text-blue-800">
                💡 Why is this important?
              </p>
              <p className="mt-2">{STEPS[step].help}</p>
            </div>
          </aside>

          <section className="flex min-h-0 min-w-0 flex-col overflow-y-auto p-5 md:p-6">
            {step === 0 && (
              <>
                <h2 className="pr-6 text-lg font-bold text-slate-800">
                  Select Company
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Choose the company for which you want to create the product DPP.
                </p>

                <label className="mt-7 text-xs font-semibold">
                  Select Company
                </label>

                <select
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="mt-2 w-full rounded-md border border-blue-300 bg-white p-3 text-sm"
                >
                  {COMPANIES.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.id})
                    </option>
                  ))}
                </select>
              </>
            )}

            {step === 1 && (
              <>
                <h2 className="text-lg font-bold text-slate-800">
                  Product DPP Creation
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Follow the guided steps to create a Digital Product Passport
                  for your product.
                </p>

                <div className="my-5 border-t border-slate-200" />

                <label className="text-xs font-semibold">
                  Choose Product Category
                </label>

                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-2 w-full rounded-md border border-blue-300 bg-white p-3 text-sm"
                >
                  <option>Battery</option>
                  <option>Electric Vehicle</option>
                  <option>Electronics</option>
                  <option>Other</option>
                </select>
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="pr-6 text-lg font-bold text-slate-800">
                  Select Template / Version
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Choose the appropriate template and version for your product.
                </p>

                <label className="mt-5 block text-xs font-semibold">
                  Template
                </label>

                <select
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  className="mt-2 w-full rounded-md border border-blue-300 bg-white p-3 text-sm"
                >
                  <option>Battery DPP Template v1.2</option>
                  <option>Battery DPP Template v1.1</option>
                </select>

                <label className="mt-4 block text-xs font-semibold">
                  Version
                </label>

                <select
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="mt-2 w-full rounded-md border border-blue-300 bg-white p-3 text-sm"
                >
                  <option>V1.2.0 (Latest)</option>
                  <option>V1.1.0</option>
                </select>

                <div className="mt-4 rounded-md bg-blue-50 p-3 text-xs text-slate-600">
                  <p className="font-semibold text-slate-800">
                    ⓘ About this template
                  </p>
                  <p className="mt-1">
                    This template follows the selected Battery DPP structure.
                  </p>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <h2 className="pr-6 text-lg font-bold text-slate-800">
                  Upload Master Data
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  Upload your master data file as per the selected template.
                </p>

                {renderTemplateCard()}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />

                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    handleFile(e.dataTransfer.files?.[0]);
                  }}
                  className="mt-4 flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-blue-300 bg-slate-50 p-4 text-center hover:bg-blue-50"
                >
                  <span className="text-3xl text-blue-600">☁</span>
                  <p className="mt-2 text-xs font-semibold text-slate-700">
                    Drag and drop your file here
                  </p>
                  <p className="mt-1 text-xs text-blue-600">
                    or click to browse
                  </p>
                </div>

                <p className="mt-2 text-center text-[10px] text-slate-500">
                  Supports .csv, .xlsx, .xls · Max size: 10 MB
                </p>

                {file && (
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-green-200 bg-green-50 p-3 text-xs">
                    <span className="break-all text-green-800">
                      Selected: {file.name}
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="shrink-0 text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                )}

                <div className="mt-4 rounded-md bg-blue-50 p-3 text-xs text-slate-600">
                  <p className="font-semibold text-slate-800">
                    ⓘ File guidelines
                  </p>
                  <ul className="mt-2 list-disc pl-5">
                    <li>
                      Ensure the file follows the selected template structure.
                    </li>
                    <li>All required fields must be filled in.</li>
                  </ul>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <h2 className="pr-6 text-lg font-bold text-slate-800">
                  System Auto-Fills Model Data
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  We’ll validate and auto-fill model details from your master
                  data. You can review and edit the values before proceeding.
                </p>

                {renderTemplateCard()}
                {renderAutoFillHeader('Model Data (Auto-Filled from Master Data)')}
                {renderTable(MODEL_FIELDS, modelData, updateModel)}
                {renderNote()}
              </>
            )}

            {step === 5 && (
              <>
                <h2 className="pr-6 text-lg font-bold text-slate-800">
                  System Auto-Fills Batch Data
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  We’ll validate and auto-fill batch details from your master
                  data. You can review and edit the values before proceeding.
                </p>

                {renderTemplateCard()}
                {renderAutoFillHeader('Batch Data (Auto-Filled from Master Data)')}
                {renderTable(BATCH_FIELDS, batchData, updateBatch)}
                {renderNote()}
              </>
            )}

            {step === 6 && (
              <>
                <h2 className="pr-6 text-lg font-bold text-slate-800">
                  System Auto-fills Product Data
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  We’ll validate and auto-fill product details from your master
                  data. You can review and edit the values before proceeding.
                </p>

                {renderTemplateCard()}
                {renderAutoFillHeader('Product Data (Auto-Filled from Master Data)')}
                {renderTable(PRODUCT_FIELDS, productData, updateProduct)}
                {renderNote()}
              </>
            )}

            {step === 7 && renderAdditionalFields()}

            <div className="mt-auto flex items-center justify-between gap-2 border-t border-slate-200 bg-white pt-4">
              <button
                type="button"
                onClick={closePage}
                className="shrink-0 rounded-md border border-slate-300 px-3 py-2 text-xs hover:bg-slate-50 sm:px-4 sm:text-sm"
              >
                Cancel
              </button>

              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  disabled={step === 0}
                  onClick={goPrevious}
                  className={`rounded-md px-3 py-2 text-xs sm:px-4 sm:text-sm ${
                    step === 0
                      ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                >
                  ← Previous
                </button>

                {step < 4 && (
                  <button
                    type="button"
                    onClick={goNext}
                    className="whitespace-nowrap rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-700 sm:px-5 sm:text-sm"
                  >
                    Next →
                  </button>
                )}

                {step >= 4 && step <= 6 && (
                  <button
                    type="button"
                    onClick={handleReview}
                    className="whitespace-nowrap rounded-md bg-blue-600 px-3 py-2 text-[10px] font-semibold text-white hover:bg-blue-700 sm:px-4 sm:text-xs"
                  >
                    Review &amp; Create DPP →
                  </button>
                )}

                {step === 7 && (
                  <button
                    type="button"
                    onClick={createDPP}
                    className="whitespace-nowrap rounded-md bg-blue-600 px-3 py-2 text-[10px] font-semibold text-white hover:bg-blue-700 sm:px-4 sm:text-xs"
                  >
                    Review &amp; Create DPP →
                  </button>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default UploadData2;